import React, { useState, useEffect } from 'react';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import PageLayout from './components/PageLayout'; 
import GuestChatWidget from './components/GuestChatWidget'; 

// --- PAGES ---
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import CoursesPage from './pages/CoursesPage'; 
import UnitsPage from './pages/UnitsPage';
import CreateClassPage from './pages/CreateClassPage';
import ManageStudentsPage from './pages/ManageStudentsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UnitLessonsPage from './pages/UnitLessonsPage';
import LessonPage from './pages/LessonPage'; 
import LessonLibrary from './pages/LessonLibrary';

const pageMapping = {
    landing: LandingPage,
    dashboard: DashboardPage,
    login: LoginPage,
    register: RegisterPage,
    manageStudents: ManageStudentsPage,
    createClass: CreateClassPage,
    profile: ProfilePage,
    resetPassword: ResetPasswordPage,
    eslCourses: CoursesPage, 
    units: UnitsPage,
    unitLessons: UnitLessonsPage,
    lessonLibrary: LessonLibrary,
};

const AppContent = () => {
    const { user, loading } = useAuth();
    const { currentPage, currentPageData } = useNavigation();

    if (loading) return <div className="flex h-screen items-center justify-center font-bold text-blue-600">Loading Session...</div>;

    const renderPage = () => {
        const isLessonPath = currentPage && currentPage.startsWith('lessons/');
        const publicPages = ['eslCourses', 'units', 'unitLessons'];

        // --- GUEST ROUTING ---
        if (!user) {
            if (currentPage === 'login') return <LoginPage />;
            if (currentPage === 'register') return <RegisterPage />;
            
            // Allow guests to see the lesson browser and specific lesson pages
            if (publicPages.includes(currentPage) || isLessonPath) {
                return (
                    <PageLayout>
                        {isLessonPath ? <LessonPage /> : React.createElement(pageMapping[currentPage], currentPageData)}
                    </PageLayout>
                );
            }
            
            // Default for guests is Landing
            return (
                <>
                    <LandingPage />
                    <GuestChatWidget />
                </>
            );
        }

        // --- AUTHENTICATED ROUTING ---
        // If logged in, don't show landing/login/register
        if (['landing', 'login', 'register'].includes(currentPage)) {
            return (
                <PageLayout>
                    <DashboardPage />
                </PageLayout>
            );
        }
        
        if (isLessonPath) {
            return (
                <PageLayout>
                    <LessonPage />
                </PageLayout>
            );
        }

        const Component = pageMapping[currentPage] || DashboardPage;
        return (
            <PageLayout>
                <Component {...currentPageData} />
            </PageLayout>
        );
    };

    return renderPage();
};

const App = () => {
    const [isHandlingToken, setIsHandlingToken] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');

        if (token) {
            localStorage.setItem('authToken', token); 
            localStorage.setItem('token', token); 
            localStorage.setItem('esl_nav_history', JSON.stringify([{ page: 'dashboard', data: null }]));
            window.history.replaceState({}, document.title, window.location.pathname);
            window.location.reload();
            return; 
        }

        setIsHandlingToken(false);
    }, []);

    if (isHandlingToken) {
        return <div className="flex h-screen items-center justify-center font-bold text-gray-500">Processing Login...</div>;
    }

    return (
        <AuthProvider>
            <ModalProvider>
                <NavigationProvider>
                    <AppContent />
                </NavigationProvider>
            </ModalProvider>
        </AuthProvider>
    );
};

export default App;